import * as React from "react";
import styled from "styled-components";
import { connect } from "react-redux";
import { toast } from "react-toastify";
import { ApplicationState } from "../../store";
import * as DepotRawDataStore from "../../store/DepotRawDataReducer";
import { RouteComponentProps } from "react-router";
import { Row, Col } from "react-grid-system";
import Box from "../Box";
import Select from "../Select";
import Button from "../Button";
import Loader from "../Loader";
import DateRangePicker from "../DateRangePicker";

import depots from "../../depots.json";
import regions from "../../regions.json";
import salesFiles from "../../salesFiles.json";
import i18next from "i18next";
import {
  isValidDateRange,
  isDateRangeWithinLimit,
  clampDateRangeToLimit,
} from "../../helpers/dateValidation";

const SELECT_ALL_DEPOTS_OPTION_VALUE = "ALL";
const MAX_DAYS_WHEN_ALL_DEPOTS_SELECTED = 31;
// depotCode indicator sent to the backend meaning "all depots" / "all depots in region <code>"
const ALL_DEPOTS_INDICATOR = "all";

// "North-East" -> "ne", "South-East" -> "se", "West" -> "west"
const getRegionCode = (regionName: string): string => {
  if (!regionName) {
    return "";
  }
  const parts = regionName.split("-");
  return parts.length > 1
    ? parts.map((part) => part.charAt(0).toLowerCase()).join("")
    : regionName.toLowerCase();
};

type DepotRawDataReportsProps = DepotRawDataStore.DepotRawDataState &
  typeof DepotRawDataStore.actionCreators &
  RouteComponentProps<{}>;

class DepotRawData extends React.PureComponent<DepotRawDataReportsProps> {
  state = {
    shouldRender: false,
    selectedRegion: "",
    selectedDepot: "",
    date: [new Date(), new Date()],
    allDepotsSelected: false,
    allRegionDepotsSelected: false,
    hasIncompleteSelection: true,
  };

  constructor(props) {
    super(props);
  }

  componentDidMount() {
    this.props.clearValues();
    this.toggleMapBtnState(true);
  }

  //shouldComponentUpdate(nextProps, nextState) {
  //	if (!nextState.shouldRender) {
  //		return false;
  //	}
  //	return true;
  //}

  onDateChange = (date) => {
    if (!isValidDateRange(date)) {
      return;
    }
    this.setState({
      date: this.isDateRangeRestricted()
        ? this.restrictDateRangeIfNeeded(date)
        : date,
    });
  };

  isDateRangeRestricted = () =>
    this.state.allDepotsSelected || this.state.allRegionDepotsSelected;

  // Safety net: send buttons stay disabled if a >31 day range ever slips
  // through the auto-clamp for an all-depots/all-region-depots selection.
  isDateRangeTooLongForSelection = () =>
    this.isDateRangeRestricted() &&
    !isDateRangeWithinLimit(
      this.state.date as [Date, Date],
      MAX_DAYS_WHEN_ALL_DEPOTS_SELECTED
    );

  restrictDateRangeIfNeeded = (date: [Date, Date]): [Date, Date] => {
    if (isDateRangeWithinLimit(date, MAX_DAYS_WHEN_ALL_DEPOTS_SELECTED)) {
      return date;
    }
    toast.warn(i18next.t("date_range_limited_to_31_days"));
    return clampDateRangeToLimit(date, MAX_DAYS_WHEN_ALL_DEPOTS_SELECTED);
  };

  toggleSelectAllDepots = () => {
    if (this.state.allDepotsSelected) {
      this.setState({ allDepotsSelected: false });
      this.props.clearValues();
      this.toggleMapBtnState(true);
      return;
    }

    this.props.clearValues();
    this.setState({
      allDepotsSelected: true,
      allRegionDepotsSelected: false,
      date: this.restrictDateRangeIfNeeded(this.state.date as [Date, Date]),
    });
    this.toggleMapBtnState(false);
  };

  onChange = (event) => {
    event.preventDefault();
    if (event.target.name === "selectedRegion") {
      var region = event.target.value;
      this.setState({ allRegionDepotsSelected: false });
      this.props.setRegion(region);
      this.toggleMapBtnState(true);
    } else if (event.target.name === "selectedDepot") {
      var depotId = event.target.value;
      if (depotId === SELECT_ALL_DEPOTS_OPTION_VALUE) {
        this.props.setDepot(SELECT_ALL_DEPOTS_OPTION_VALUE);
        this.setState({
          allRegionDepotsSelected: true,
          date: this.restrictDateRangeIfNeeded(this.state.date as [Date, Date]),
        });
        this.toggleMapBtnState(false);
        return;
      }
      this.setState({ allRegionDepotsSelected: false });
      this.props.setDepot(depotId);
      if (depotId && this.props.selectedRegion && this.state.date) {
        this.toggleMapBtnState(false);
      }
    }
  };

  getReportDepotCode = (): string => {
    if (this.state.allDepotsSelected) {
      return ALL_DEPOTS_INDICATOR;
    }
    if (this.state.allRegionDepotsSelected) {
      return `${ALL_DEPOTS_INDICATOR}-${getRegionCode(this.props.selectedRegion)}`;
    }
    return this.props.selectedDepot;
  };

  getRawSales = (event) => {
    var depotData = {
      fromDate: this.state.date[0],
      toDate: this.state.date[1],
      depotCode: this.getReportDepotCode(),
      reportType: "sales",
    };
    this.props.getSalesDataForDepot(depotData);
  };

  getRawStock = (event) => {
    var depotData = {
      fromDate: this.state.date[0],
      toDate: this.state.date[1],
      depotCode: this.getReportDepotCode(),
      reportType: "stock",
    };
    this.props.getStockDataForDepot(depotData);
  };

  toggleMapBtnState = (state) => {
    this.setState({ hasIncompleteSelection: state });
  };

  // Send buttons stay disabled while the depot/region selection is
  // incomplete, or while a >31 day range is selected together with
  // all-depots/all-region-depots (belt-and-suspenders alongside the clamp).
  isSendDisabled = () =>
    this.state.hasIncompleteSelection || this.isDateRangeTooLongForSelection();

  resetBtnState() {
    let buttons = document.getElementsByTagName("button");
    for (let i = 0; i < buttons.length; i++) {
      buttons[i].disabled = false;
    }
  }

  public render() {
    const { depotsList, regionsList, isLoading } = this.props;
    const { allDepotsSelected } = this.state;
    var regs = regions
        ? regions.map((region, id) => {
            return {
              value: region.name,
              label: region.name,
            };
          })
        : [],
      depots = depotsList
        ? depotsList.map((depot, id) => {
            return {
              value: depot.depotCode,
              label: depot.depotName,
            };
          })
        : [];

    if (
      depots.length &&
      this.props.selectedRegion &&
      this.props.selectedRegion !== "-1"
    ) {
      depots = [
        { value: SELECT_ALL_DEPOTS_OPTION_VALUE, label: i18next.t("select_all") },
        ...depots,
      ];
    }

    const BoxContent = styled.div`
      padding: 30px;
    `;

    return (
      <>
        <Box
          title={i18next.t("depot_raw_data")}
          buttons={
            <>
              <Button
                id="selectAllDepots"
                size="large"
                onClick={this.toggleSelectAllDepots}
              >
                {allDepotsSelected
                  ? i18next.t("deselect_all")
                  : i18next.t("select_all")}
              </Button>
              <Button
                id="getRawSales"
                size="large"
                disabled={this.isSendDisabled()}
                title={
                  this.isDateRangeTooLongForSelection()
                    ? i18next.t("date_range_limited_to_31_days")
                    : undefined
                }
                onClick={this.getRawSales}
              >
                {i18next.t("sales")}
              </Button>
              <Button
                id="getRawStock"
                size="large"
                disabled={this.isSendDisabled()}
                title={
                  this.isDateRangeTooLongForSelection()
                    ? i18next.t("date_range_limited_to_31_days")
                    : undefined
                }
                onClick={this.getRawStock}
              >
                {i18next.t("stock")}
              </Button>
            </>
          }
        >
          <BoxContent>
            <Row>
              <Col sm={4}>
                <DateRangePicker
                  label={i18next.t("select_date")}
                  onChange={this.onDateChange}
                  value={this.state.date}
                />
              </Col>

              <Col sm={4}>
                <Select
                  label={i18next.t("region")}
                  name="selectedRegion"
                  value={this.props.selectedRegion}
                  defaultValue={"-1"}
                  disabled={allDepotsSelected}
                  onChange={this.onChange}
                  options={regs}
                />
              </Col>

              <Col sm={4}>
                <Select
                  label={i18next.t("selected_depot")}
                  name="selectedDepot"
                  value={this.props.selectedDepot}
                  defaultValue={"-1"}
                  disabled={allDepotsSelected}
                  onChange={this.onChange}
                  options={depots}
                />
              </Col>
            </Row>
          </BoxContent>
        </Box>
        <Loader background="semiLight" isLoading={isLoading} />
      </>
    );
  }
}
export default connect(
  (state: ApplicationState) => state.depotRawDataReports,
  DepotRawDataStore.actionCreators
)(DepotRawData);
